<template>
  <div class="page-container team-page">
    <!-- 左侧小组列表 -->
    <div class="left-panel">
      <div class="panel-header">
        <span class="panel-title">人员小组</span>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAddTeam">新建小组</el-button>
      </div>

      <!-- 搜索 -->
      <div class="list-search">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索小组名称"
          clearable
          size="small"
          prefix-icon="el-icon-search"
          @keyup.enter.native="handleQuery"
          @clear="handleQuery"
        />
        <el-select v-model="searchType" size="small" placeholder="类型" clearable @change="handleQuery">
          <el-option label="巡查测试小组" value="task"/>
          <el-option label="维修小组" value="fault"/>
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">查询</el-button>
      </div>

      <!-- 小组卡片列表 -->
      <div v-loading="listLoading" class="team-list">
        <div
          v-for="item in teamList"
          :key="item.uuid"
          class="team-card"
          :class="{ active: currentTeam && currentTeam.uuid === item.uuid }"
          @click="handleTeamClick(item)"
        >
          <div class="team-card-info">
            <div class="team-type-badge" :class="item.teamType">{{ teamTypeLabel(item.teamType) }}</div>
            <div class="team-name">{{ item.teamName }}</div>
          </div>
          <div class="team-card-meta">
            <span class="member-count">{{ item.memberCount || 0 }} 人</span>
          </div>
        </div>
        <div v-if="!listLoading && teamList.length === 0" class="empty-placeholder">
          <i class="el-icon-user" />
          <p>暂无小组</p>
        </div>
      </div>
    </div>

    <!-- 右侧成员详情面板 -->
    <div class="right-panel">
      <div v-if="!currentTeam" class="empty-placeholder center">
        <i class="el-icon-mouse" />
        <p>请在左侧选择小组</p>
      </div>

      <template v-else>
        <!-- 小组信息头 -->
        <div class="panel-header">
          <div class="team-info-title">
            <span class="panel-title">{{ currentTeam.teamName }}</span>
            <el-tag size="small" :type="typeTagColor(currentTeam.teamType)" effect="plain" style="margin-left: 8px;">
              {{ teamTypeLabel(currentTeam.teamType) }}
            </el-tag>
          </div>
          <div class="header-actions">
            <el-button type="primary" size="mini" plain icon="el-icon-edit" @click="handleEditTeam">编辑</el-button>
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAddMember">添加成员</el-button>
            <el-button type="danger" size="mini" icon="el-icon-delete" @click="handleDeleteTeam">解散小组</el-button>
          </div>
        </div>

        <!-- 备注 -->
        <div v-if="currentTeam.remark" class="team-remark">
          <i class="el-icon-info" /> {{ currentTeam.remark }}
        </div>

        <!-- 成员搜索条 -->
        <div class="member-search-bar">
          <el-input
            v-model="memberKeyword"
            placeholder="姓名 / 手机号"
            clearable
            size="small"
            prefix-icon="el-icon-search"
            style="width: 200px"
            @keyup.enter.native="handleMemberSearch"
            @clear="handleMemberReset"
          />
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleMemberSearch">查询</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="handleMemberReset">重置</el-button>
          <span class="member-count-tip">共 {{ memberPagination.total }} 人</span>
        </div>

        <!-- 成员表格容器 -->
        <div class="member-table-wrapper">
          <el-table
            v-loading="memberLoading"
            :data="memberList"
            border
            size="small"
            height="100%"
          >
            <el-table-column prop="userId" label="账号" min-width="120" align="center"/>
            <el-table-column label="姓名" prop="userName" min-width="140" align="center">
              <template slot-scope="scope">
                <span>{{ scope.row.userName }}</span>
                <el-tag v-if="scope.row.isLeader" size="mini" type="warning" style="margin-left: 6px;">组长</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="tel" label="手机号码" min-width="130" align="center"/>
            <el-table-column prop="joinTime" label="加入时间" width="160" align="center">
              <template slot-scope="scope">
                {{ formatDateTime(scope.row.joinTime) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="160" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button v-if="!scope.row.isLeader" type="warning" plain size="mini" @click="handleSetLeader(scope.row)">设为组长</el-button>
                <el-button type="danger" plain size="mini" @click="handleRemoveMember(scope.row)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 成员分页 -->
        <el-pagination
          v-if="memberPagination.total > 0"
          class="member-pagination"
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="memberPagination.pageNo"
          :page-size="memberPagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="memberPagination.total"
          @current-change="handleMemberPageChange"
          @size-change="handleMemberSizeChange"
        />
      </template>
    </div>

    <!-- 新建/编辑小组弹窗 -->
    <TeamDialog
      :visible.sync="teamDialogVisible"
      :team-data="editingTeam"
      @success="handleTeamSaved"
    />

    <!-- 添加成员弹窗 -->
    <MemberSelectDialog
      :visible.sync="memberDialogVisible"
      :team-id="currentTeam ? currentTeam.uuid : ''"
      :team-type="currentTeam ? currentTeam.teamType : 'task'"
      @success="handleTeamSaved"
    />
  </div>
</template>

<script>
// 服务
import { teamService } from './team.service';
// 工具函数
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
// 组件
import TeamDialog from './teamDialog.vue';
import MemberSelectDialog from './memberSelectDialog.vue';

export default {
  name: 'TeamList',
  components: { TeamDialog, MemberSelectDialog },
  data () {
    return {
      // 左侧列表
      teamList: [],
      listLoading: false,
      searchKeyword: '',
      searchType: '',

      // 右侧详情
      currentTeam: null,
      memberList: [],
      memberLoading: false,
      memberKeyword: '', // 成员搜索关键词（姓名/手机号）
      memberPagination: { pageNo: 1, pageSize: 20, total: 0 }, // 成员分页

      // 弹窗控制
      teamDialogVisible: false,
      editingTeam: null,
      memberDialogVisible: false
    };
  },
  computed: {
    // 不再需要前端过滤，已改为后端搜索
  },
  methods: {
    // ==================== 小组列表 ====================

    loadTeamList () {
      this.listLoading = true;
      // 不传分页参数，或使用一个很大的pageSize来获取所有数据
      teamService.queryTeamList({
        teamName: this.searchKeyword || '',
        teamType: this.searchType || ''
      }).then(res => {
        const data = res.data;
        this.teamList = data || [];
      }).finally(() => {
        this.listLoading = false;
      });
    },

    /** 左侧小组列表查询（按钮点击 / 下拉切换 / 回车触发） */
    handleQuery () {
      this.loadTeamList();
    },

    /** 右侧成员搜索：提交关键词触发后端查询 */
    handleMemberSearch () {
      this.memberPagination.pageNo = 1;
      this.loadMemberList();
    },

    /** 右侧成员重置：清空关键词并重新查询 */
    handleMemberReset () {
      this.memberKeyword = '';
      this.memberPagination.pageNo = 1;
      this.loadMemberList();
    },

    handlePageChange (page) {
      this.pagination.pageNo = page;
      this.loadTeamList();
    },

    handleTeamClick (team) {
      this.currentTeam = team;
      // 切换小组时重置成员搜索状态和分页
      this.memberKeyword = '';
      this.memberPagination.pageNo = 1;
      this.loadMemberList();
    },

    handleMemberPageChange (page) {
      this.memberPagination.pageNo = page;
      this.loadMemberList();
    },

    handleMemberSizeChange (size) {
      this.memberPagination.pageSize = size;
      this.memberPagination.pageNo = 1;
      this.loadMemberList();
    },

    // ==================== 小组成员 ====================

    loadMemberList () {
      if (!this.currentTeam) return;
      this.memberLoading = true;
      teamService.queryTeamMemberPage({
        teamId: this.currentTeam.uuid,
        memberKeyword: this.memberKeyword || '',
        pageNo: this.memberPagination.pageNo,
        pageSize: this.memberPagination.pageSize
      }).then(res => {
        const data = res.data;
        this.memberList = data.list || data.rows || [];
        this.memberPagination.total = data.total || 0;
      }).finally(() => {
        this.memberLoading = false;
      });
    },

    handleAddMember () {
      this.memberDialogVisible = true;
    },

    handleSetLeader (member) {
      this.$confirm(`确认将"${member.userName}"设为组长？`, '提示', { type: 'warning' }).then(() => {
        this.memberLoading = true;
        teamService.setLeader({ teamId: this.currentTeam.uuid, userId: member.userId }).then(() => {
          this.$message.success('设置成功');
          this.loadMemberList();
        }).finally(() => {
          this.memberLoading = false;
        });
      }).catch(() => {});
    },

    handleRemoveMember (member) {
      this.$confirm(`确认将"${member.userName}"从本小组移除？`, '提示', { type: 'warning' }).then(() => {
        this.memberLoading = true;
        teamService.removeMember({ teamId: this.currentTeam.uuid, userId: member.userId }).then(() => {
          this.$message.success('移除成功');
          this.loadMemberList();
          this.loadTeamList();
        }).finally(() => {
          this.memberLoading = false;
        });
      }).catch(() => {});
    },

    // ==================== 小组 CRUD ====================

    handleAddTeam () {
      this.editingTeam = null;
      this.teamDialogVisible = true;
    },

    handleEditTeam () {
      this.editingTeam = { ...this.currentTeam };
      this.teamDialogVisible = true;
    },

    handleTeamSaved () {
      this.loadTeamList();
      if (this.currentTeam) this.loadMemberList();
    },

    handleDeleteTeam () {
      this.$confirm(`确认解散小组"${this.currentTeam.teamName}"？解散后成员信息将全部清空，已分配该小组的任务不受影响。`, '解散确认', {
        type: 'warning',
        confirmButtonText: '确认解散',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        this.listLoading = true;
        teamService.deleteTeam(this.currentTeam.uuid).then(() => {
          this.$message.success('已解散');
          this.currentTeam = null;
          this.memberList = [];
          this.loadTeamList();
        }).finally(() => {
          this.listLoading = false;
        });
      }).catch(() => {});
    },

    // ==================== 工具方法 ====================

    teamTypeLabel (type) {
      return { task: '巡查测试', fault: '维修' }[type] || type;
    },

    typeTagColor (type) {
      return { task: '', fault: 'warning' }[type] || 'info';
    },

    formatDateTime
  },
  mounted () {
    this.loadTeamList();
  }
};
</script>

<style scoped lang="scss">
.team-page {
  display: flex;
  height: calc(100vh - 125px);
  background: #f5f7fa;
  padding: 15px;
  gap: 15px;
  box-sizing: border-box;

  .left-panel {
    width: 380px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    .panel-header {
      padding: 12px 15px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;

      .panel-title {
        font-size: 15px;
        font-weight: 600;
        color: #303133;
      }
    }

    .list-search {
      padding: 10px 12px 8px;
      flex-shrink: 0;
      display: flex;
      gap: 8px;
      align-items: center;

      .el-input { flex: 1; min-width: 0; }
      .el-select { width: 90px; flex-shrink: 0; }
    }

    .team-list {
      flex: 1;
      overflow-y: auto;
      padding: 8px;

      .team-card {
        padding: 10px 12px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        transition: all 0.2s;

        &:hover { border-color: #2296f7; }
        &.active { background: #ecf5ff; border-color: #2296f7; }

        .team-card-info {
          display: flex;
          align-items: center;
          gap: 8px;

          .team-type-badge {
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
            flex-shrink: 0;

            &.task { background: #ecf5ff; color: #409eff; }
            &.fault { background: #fdf6ec; color: #e6a23c; }
          }

          .team-name { font-size: 13px; color: #303133; }
        }

        .member-count {
          font-size: 12px;
          color: #909399;
          white-space: nowrap;
        }
      }
    }
  }

  .right-panel {
    flex: 1;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    padding: 15px 20px;
    overflow: hidden;  // 防止整体溢出

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 12px;
      border-bottom: 1px solid #ebeef5;
      margin-bottom: 10px;
      flex-shrink: 0;

      .team-info-title {
        display: flex;
        align-items: center;
      }

      .panel-title { font-size: 16px; font-weight: 600; color: #303133; }
      .header-actions { display: flex; gap: 8px; }
    }

    .team-remark {
      font-size: 13px;
      color: #909399;
      background: #f5f7fa;
      border-radius: 4px;
      padding: 6px 10px;
      margin-bottom: 8px;
    }

    .member-search-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #ebeef5;
      margin-bottom: 8px;
      flex-shrink: 0;

      .member-count-tip {
        margin-left: auto;
        font-size: 13px;
        color: #909399;
      }
    }

    // 表格容器，flex布局，自动占据剩余空间
    .member-table-wrapper {
      flex: 1;
      overflow: hidden;
      min-height: 200px;  // 最小高度，确保至少显示几行
    }

    .member-pagination {
      padding: 15px 0 5px;
      text-align: right;
      flex-shrink: 0;
      margin-top: 10px;
    }
  }

  .empty-placeholder {
    padding: 40px 0;
    text-align: center;
    color: #909399;

    &.center {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    i { font-size: 48px; margin-bottom: 10px; color: #E4E7ED; }
    p { font-size: 14px; margin: 0; }
  }
}
</style>

<style lang="scss">
.el-message { z-index: 10000 !important; }
.el-message-box__wrapper { z-index: 10001 !important; }
</style>
